import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../store/AuthContext";
import { useQuery } from "../../hooks/queries/useQuery";
import {
  Empty,
  ErrorNotice,
  Loading,
} from "../../components/common/Primitives";
import { display, formatDate } from "../../utils/format";
import type { Row } from "../../config/types";
import StyledSelect from "../../components/common/StyledSelect";
interface LoginCount {
  label: string;
  count: number;
}
interface Dashboard {
  today: string;
  monthlyVisitors: number | null;
  pendingInterviews: number | null;
  interviews: Row[];
  loginsByRole: LoginCount[];
  loginsByUser: LoginCount[];
}
function LoginChart({ title, rows }: { title: string; rows: LoginCount[] }) {
  const { t } = useTranslation();
  const maximum = Math.max(1, ...rows.map((r) => r.count));
  return (
    <section className="panel">
      <div className="panel-heading">
        <h2>{t(title)}</h2>
      </div>
      <div className="login-chart" aria-label={t(title)}>
        {rows.length ? (
          rows.map((row) => (
            <div className="login-chart-row" key={row.label}>
              <span>{t(row.label)}</span>
              <meter
                min={0}
                max={maximum}
                value={row.count}
                aria-label={t(row.label)}
              />
              <strong>{row.count}</strong>
            </div>
          ))
        ) : (
          <Empty title={t("No login history yet")} />
        )}
      </div>
    </section>
  );
}
export default function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data, loading, error, reload } = useQuery<Dashboard>("/dashboard");
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("date");
  const [asc, setAsc] = useState(false);
  if (error) return <ErrorNotice message={error} retry={reload} />;
  if (loading || !data) return <Loading />;
  const columns = [
    { key: "firstName", label: "First name" },
    { key: "lastName", label: "Last name" },
    { key: "location", label: "Location" },
    { key: "date", label: "Date" },
    { key: "phone", label: "Phone number" },
  ];
  const rows = data.interviews
    .filter((row) =>
      columns.some((c) =>
        display(row, c.key)
          .toLocaleLowerCase()
          .includes(search.toLocaleLowerCase()),
      ),
    )
    .sort(
      (a, b) =>
        String(a[sort] || "").localeCompare(String(b[sort] || "")) *
        (asc ? 1 : -1),
    );
  return (
    <>
      <div className="page-heading">
        <div>
          <h1>{t("Hello, {{name}}", { name: user!.firstName })}.</h1>
          <p>{formatDate(data.today)}</p>
        </div>
      </div>
      {user!.role === "Admin" && (
        <div className="legacy-stats">
          <section className="panel">
            <h2>{t("Pending interviews")}</h2>
            <strong>{data.pendingInterviews}</strong>
          </section>
          <section className="panel">
            <h2>{t("Visitors this month")}</h2>
            <strong>{data.monthlyVisitors}</strong>
          </section>
        </div>
      )}
      {["Admin", "RH", "Assistente"].includes(user!.role) && (
        <section className="panel records-panel">
          <div className="panel-heading">
            <h2>{t("Pending interviews")}</h2>
          </div>
          <div className="table-toolbar">
            <label>
              {t("Search interviews")}
              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </label>
            <label>
              {t("Rows per page")}
              <StyledSelect
                label={t("Rows per page")}
                value={String(limit)}
                options={[10, 25, 50, 100].map((number) => ({
                  value: String(number),
                  label: String(number),
                }))}
                onChange={(value) => {
                  setLimit(Number(value));
                  setPage(1);
                }}
              />
            </label>
          </div>
          {rows.length ? (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    {columns.map((c) => (
                      <th key={c.key}>
                        <button
                          className="text-button"
                          onClick={() => {
                            setSort(c.key);
                            setAsc(sort === c.key ? !asc : true);
                          }}
                        >
                          {t(c.label)}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice((page - 1) * limit, page * limit).map((row) => (
                    <tr key={row.id}>
                      {columns.map((c) => (
                        <td key={c.key}>{display(row, c.key)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty title={t("No pending interviews")} />
          )}
          <div className="table-footer">
            <button
              className="button secondary"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              {t("Previous page")}
            </button>
            <span>
              {t("Page {{page}} of {{total}}", {
                page,
                total: Math.max(1, Math.ceil(rows.length / limit)),
              })}
            </span>
            <button
              className="button secondary"
              disabled={page * limit >= rows.length}
              onClick={() => setPage(page + 1)}
            >
              {t("Next page")}
            </button>
          </div>
        </section>
      )}
      {user!.role === "Admin" && (
        <div className="legacy-login-charts">
          <LoginChart title="Logins by role" rows={data.loginsByRole} />
          <LoginChart title="Logins by user" rows={data.loginsByUser} />
        </div>
      )}
    </>
  );
}
